// Normalized phone verification result
export interface NormalizedPhoneResult {
  isValid: boolean;
  normalized: string;
  formattedLocal: string;
  error?: string;
}

// Normalizes phone numbers to standard E.164 international format
export function normalizePhoneNumber(
  rawPhone: string,
  countryCode: string = 'MA'
): NormalizedPhoneResult {
  if (!rawPhone) {
    return { isValid: false, normalized: '', formattedLocal: '', error: 'Phone number is required' };
  }

  // Strip whitespace and special characters
  let cleaned = rawPhone.replace(/[\s\-\(\)\.]/g, '');

  // Convert international double-zero prefix to standard plus
  if (cleaned.startsWith('00')) {
    cleaned = '+' + cleaned.substring(2);
  }

  const cCode = countryCode.toUpperCase();

  // Handle Morocco specific formatting and prefixes
  if (cCode === 'MA') {
    if (cleaned.startsWith('+212')) {
      // Already prefixed
    } else if (cleaned.startsWith('212')) {
      cleaned = '+' + cleaned;
    } else if (cleaned.startsWith('0')) {
      cleaned = '+212' + cleaned.substring(1);
    } else if (cleaned.length === 9 && (cleaned.startsWith('6') || cleaned.startsWith('7') || cleaned.startsWith('5'))) {
      cleaned = '+212' + cleaned;
    }
    // "+212 06 12 34 56 78" keeps the national 0 after the country code, as many shoppers and COD apps write it;
    // the 0 is dropped so it is the same number, and the same customer, as "06 12 34 56 78".
    if (/^\+2120[5-8]\d{8}$/.test(cleaned)) cleaned = '+212' + cleaned.substring(5);

    const moroccoRegex = /^\+212[5-8]\d{8}$/;
    const isValid = moroccoRegex.test(cleaned);

    if (!isValid) {
      return {
        isValid: false,
        normalized: cleaned,
        formattedLocal: rawPhone,
        error: 'Invalid Moroccan phone number format.'
      };
    }

    const nationalPart = '0' + cleaned.substring(4);
    const formattedLocal = nationalPart.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5');

    return { isValid: true, normalized: cleaned, formattedLocal };
  }

  // Handle general international E.164 numbers
  if (!cleaned.startsWith('+')) {
    cleaned = '+' + cleaned;
  }

  const internationalRegex = /^\+[1-9]\d{6,14}$/;
  const isValid = internationalRegex.test(cleaned);

  return {
    isValid,
    normalized: cleaned,
    formattedLocal: cleaned,
    error: isValid ? undefined : 'Invalid international phone number'
  };
}

// Convenience wrapper for Moroccan numbers
export const normalizeMoroccanPhone = (phone: string): NormalizedPhoneResult =>
  normalizePhoneNumber(phone, 'MA');

// Writes a stored number in Shopify's grouped way, "+212 6 22 88 95 97", or as stored when it is not Moroccan.
export const phoneInternational = (stored: string): string => {
  const { isValid, normalized } = normalizeMoroccanPhone(stored);
  const national = /^\+212(\d{9})$/.exec(normalized)?.[1];
  return isValid && national ? `+212 ${national[0]} ${national.slice(1).replace(/(\d{2})(?=\d)/g, '$1 ')}` : stored;
};
